<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch } from "vue";
import type { InputInstance } from "element-plus";
import { PermissionNames } from "~/configs/permission_names";
import { useOrdersStore } from "~/projects/order-manager/stores/ordersStore";
import { OriginType } from "~/types/enums/originType";
import type { Template } from "~/types/replyTemplates";
import { useReplyTemplatesStore } from "~/projects/help-desk/stores/replyStore";
import { useInboxStore } from "~/projects/help-desk/stores/inboxStore";
import { useUsersStore } from "~/stores/usersStore";
import { useAuthStore } from "~/stores/auth";
import { useTemplatesPersistFilter } from "~/projects/help-desk/stores/templatePersistFilter";
import { useConfigStore } from "~/stores/configStore";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const inboxStore = useInboxStore();
const ordersStore = useOrdersStore();
const replyTemplatesStore = useReplyTemplatesStore();
const templatesPersistFilter = useTemplatesPersistFilter();
const configStore = useConfigStore();

// Same gate as the Reply Templates page: without "show others" the picker only
// ever lists the agent's own templates, so the All / For Me switch is hidden.
const canShowOthers = computed(() =>
  isUserPermitted(PermissionNames.REPLY_TEMPLATE_SHOW_OTHERS),
);

const props = defineProps({
  fromPrivateReply: { type: Boolean, default: false },
  // SMS mode: opened from the Send SMS dialog, where a template can only
  // contribute text — the gateway carries no media. Attachments are reduced to
  // a "will not be sent" note, attachment-only templates drop out of the list,
  // the order tab is irrelevant, and the immediate Send action is gone: the
  // picked body lands in the SMS textarea and the SMS dialog's own Send button
  // is the only way a message leaves.
  fromSms: { type: Boolean, default: false },
});
const emit = defineEmits([
  "set-message",
  "close-modal",
  "set-ordermessage",
  "set-message-for-edit",
]);

const currentTab = ref("template");
const searchQuery = ref("");
const categoryId = ref<number | null>(null);
const page = ref(1);
const loadingMore = ref(false);

// ✅ selection states
const selectedTemplate = ref<Template | null>(null);
const currentIndex = ref<number>(-1);
const templateRefs = ref<HTMLElement[]>([]);
const searchRef = ref<InputInstance | null>(null);

onMounted(async () => {
  if (!canShowOthers.value) {
    templatesPersistFilter.forMe = true;
  }

  if (templatesPersistFilter.forMe) {
    replyTemplatesStore._filterData.assign_to = authStore.user?.id;

    await replyTemplatesStore.getTemplates();
  } else if (
    route.query?.assign_to &&
    route.query?.assign_to.length === 1 &&
    Number(route.query?.assign_to) === authStore.user.id
  ) {
    if (!templatesPersistFilter.forMe) {
      templatesPersistFilter.forMe = true;
    }

    await replyTemplatesStore.getTemplates();
  } else {
    templatesPersistFilter.forMe = false;

    await replyTemplatesStore.getTemplates();
  }
  nextTick(() => {
    templateRefs.value = templateRefs.value.slice(
      0,
      filteredTemplates.value.length,
    );
  });
  nextTick(() => {
    focusSearch();
  });
});

const focusSearch = () => {
  searchRef.value?.focus();
};

// Load initial templates
const fetchTemplates = async (pageNo = 1) => {
  const filterData = { ...replyTemplatesStore._filterData };

  if (categoryId.value) {
    filterData.category_id = categoryId.value;
  } else {
    delete filterData.category_id;
  }

  replyTemplatesStore.setFilterData(filterData);

  await replyTemplatesStore.getTemplates(
    pageNo,
    "updated_at",
    false,
    pageNo > 1,
    10,
    searchQuery.value,
    templatesPersistFilter.forMe ? authStore.user?.id : null,
  );
};

// infinite scroll loader
const loadMore = async () => {
  const { current_page, last_page } = replyTemplatesStore._pagination;
  if (loadingMore.value || current_page >= last_page) return;

  loadingMore.value = true;
  await fetchTemplates(current_page + 1);
  loadingMore.value = false;
};

// The list normally renders only while the inbox's templates dialog is open,
// because this component outlives that dialog. The SMS dialog owns its own
// visibility and mounts a fresh copy each time it opens, so in SMS mode the
// gate is always open.
const showList = computed(
  () => props.fromSms || replyTemplatesStore.showTemplatesListDialog,
);

const smsBodies = (tpl: Template): string[] =>
  (tpl.templates ?? []).filter((body) => body?.trim());

// Attachments a pick would leave behind, shown as a count in SMS mode.
const droppedAttachmentCount = (tpl: Template): number =>
  (tpl.gallery_items?.length ?? 0) + (tpl.attachments?.length ?? 0);

const filteredTemplates = computed(() => {
  const rows = replyTemplatesStore.tableData;
  // An attachment-only template has nothing an SMS could carry, so it is not
  // offered at all rather than handing back an empty message.
  return props.fromSms ? rows.filter((tpl) => smsBodies(tpl).length) : rows;
});
const templatesWithRandomPreview = computed(() => {
  return filteredTemplates.value.map((tpl) => {
    // In SMS mode the roll only ever picks a body that has text in it, so a
    // mixed template can never preview (or send) as blank.
    const list = props.fromSms ? smsBodies(tpl) : tpl.templates || [];
    const randomOne =
      list.length > 1
        ? list[Math.floor(Math.random() * list.length)]
        : list[0] || "";

    return {
      ...tpl,
      randomTemplate: randomOne,
    };
  });
});

watch([searchQuery, categoryId], () => {
  page.value = 1;
  fetchTemplates();
});

const updateTemplateFilterAgent = async () => {
  const filterData = { ...replyTemplatesStore._filterData };

  if (templatesPersistFilter.forMe) {
    filterData.assign_to = authStore.user?.id;
  } else {
    delete filterData.assign_to;
  }

  if (categoryId.value) {
    filterData.category_id = categoryId.value;
  } else {
    delete filterData.category_id;
  }

  replyTemplatesStore.setFilterData(filterData);
  await replyTemplatesStore.getTemplates();
};

const setAutoReplyMessage = (
  template: Template & { randomTemplate?: string },
) => {
  const templateIndex = filteredTemplates.value.findIndex(
    (t) => t.id === template.id,
  );

  if (selectedTemplate.value?.id === template.id) {
    selectedTemplate.value = null;
    currentIndex.value = -1;
    return;
  }

  const modifiedTemplate = {
    ...template,
    templates: template.randomTemplate ? [template.randomTemplate] : [],
  };

  selectedTemplate.value = modifiedTemplate;
  console.log(selectedTemplate.value, "template with one");

  if (templateIndex !== -1) {
    currentIndex.value = templateIndex;
  }
};

const sendTemplate = () => {
  if (!selectedTemplate.value) return;
  emit("set-message", selectedTemplate.value);
  searchQuery.value = "";
  selectedTemplate.value = null;
  currentIndex.value = -1;
};

const sendTemplateForEdit = () => {
  if (!selectedTemplate.value) return;
  emit("set-message-for-edit", selectedTemplate.value);

  searchQuery.value = "";
  selectedTemplate.value = null;
  currentIndex.value = -1;
};

// ✅ keyboard navigation
const scrollToSelected = (index: number) => {
  nextTick(() => {
    if (templateRefs.value[index]) {
      templateRefs.value[index].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  });
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (templatesWithRandomPreview.value.length === 0) return;

  if (event.key === "ArrowDown") {
    if (currentIndex.value < templatesWithRandomPreview.value.length - 1) {
      currentIndex.value++;
      setAutoReplyMessage(templatesWithRandomPreview.value[currentIndex.value]);
      scrollToSelected(currentIndex.value);
    }
  } else if (event.key === "ArrowUp") {
    if (currentIndex.value > 0) {
      currentIndex.value--;
      setAutoReplyMessage(templatesWithRandomPreview.value[currentIndex.value]);
      scrollToSelected(currentIndex.value);
    } else if (currentIndex.value === 0) {
      currentIndex.value = -1;
      selectedTemplate.value = null;
      searchRef.value?.focus();
    }
  } else if (event.key === "Enter" && selectedTemplate.value) {
    sendTemplateForEdit();
  }
};

// Ensure templateRefs are correctly populated after rendering

watch(filteredTemplates, (newTemplates) => {
  currentIndex.value = -1;
  selectedTemplate.value = null;
});

// order
const handleCreateOrder = () => {
  ordersStore.setSelectedProfileForOrder(inboxStore.selectedTicketProfile);
  ordersStore.setOrderableData({
    source_type: OriginType.ticket,
    source_id: inboxStore.selectedTicket.id,
  });
};
watch(
  () => currentTab.value,
  (newTab) => {
    if (newTab === "order") {
      handleCreateOrder();
    } else if (newTab === "template") {
      nextTick(() => {
        focusSearch();
      });
    }
  },
);
</script>

<template>
  <el-tabs v-model="currentTab">
    <!-- ✅ Tab 1: Reply Template -->
    <el-tab-pane label="Reply Template" name="template">
      <div
        class="flex flex-col gap-3 mb-2 sm:flex-row sm:justify-center sm:items-center"
      >
        <el-input
          ref="searchRef"
          v-model="searchQuery"
          placeholder="Search by ID, Title"
          clearable
          class="w-full flex-1"
          @keydown.up.prevent="handleKeyDown"
          @keydown.down.prevent="handleKeyDown"
          @keydown.enter.prevent="handleKeyDown"
        />
        <div class="w-full sm:w-[220px] shrink-0">
          <el-select
            v-model="categoryId"
            placeholder="Select category"
            clearable
            filterable
            class="w-full"
          >
            <el-option
              v-for="item in configStore.globalConfig.reply_template_categories"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </div>

        <div v-if="canShowOthers" class="shrink-0 flex items-center">
          <el-switch
            :key="templatesPersistFilter.forMe"
            v-model="templatesPersistFilter.forMe"
            size="small"
            class="min-w-[90px] whitespace-nowrap"
            inactive-text="All"
            active-text="For Me"
            @change="updateTemplateFilterAgent"
          />
        </div>
      </div>

      <div
        v-if="showList"
        v-infinite-scroll="loadMore"
        class="h-[400px] overflow-y-auto relative"
        :infinite-scroll-disabled="loadingMore"
        infinite-scroll-distance="50"
      >
        <div
          v-for="(data, index) in templatesWithRandomPreview"
          :key="data.id"
          :ref="(el) => (templateRefs[index] = el)"
          class="px-2 cursor-pointer mb-3 border rounded-md p-2 break-words"
          :class="{
            'bg-gray-300 dark:bg-light-purple-bar':
              selectedTemplate?.id === data.id,
          }"
          tabindex="0"
          @click="setAutoReplyMessage(data)"
          @keydown.enter.prevent="
            selectedTemplate?.id === data.id && sendTemplateForEdit()
          "
        >
          <div class="flex flex-col gap-1">
            <div><b>Title:</b> {{ data.title }}</div>
            <div>
              <b>Template:</b>
              <span class="break-words">{{ data.randomTemplate }}</span>
            </div>

            <!-- SMS carries text only: say what gets left behind instead of
                 showing files that cannot travel. -->
            <div
              v-if="fromSms && droppedAttachmentCount(data)"
              class="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"
            >
              <Svgs name="info" class="w-3 h-3 shrink-0" />
              {{ droppedAttachmentCount(data) }} attachment{{
                droppedAttachmentCount(data) > 1 ? "s" : ""
              }}
              will not be sent with an SMS
            </div>

            <div v-if="!fromSms && data.gallery_items?.length">
              <b>Attachments:</b>
              <div class="flex flex-wrap gap-2">
                <img
                  v-for="item in data.gallery_items"
                  :key="item.id"
                  class="w-[5rem] h-[5rem] object-cover"
                  :src="item.attachment?.public_url"
                  :alt="item.attachment?.name"
                />
              </div>
            </div>
            <div
              v-if="!fromSms && data.attachments?.length && !fromPrivateReply"
              class="mt-2"
            >
              <b>Attachments:</b>
              <div class="grid grid-cols-4 gap-2 mt-1">
                <img
                  v-for="att in data.attachments"
                  :key="att.id"
                  :src="att.public_url"
                  class="w-[5rem] h-[5rem] object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Loader -->
        <div v-if="loadingMore" class="text-center py-3 text-gray-400">
          Loading more...
        </div>
        <div
          v-if="
            replyTemplatesStore._pagination.current_page >=
            replyTemplatesStore._pagination.last_page
          "
          class="text-center py-3 text-gray-400"
        >
          No more templates
        </div>
      </div>

      <!-- ✅ Buttons -->
      <!-- SMS mode: only "Edit and Send", which drops the body into the SMS
           textarea. There is no direct Send here — nothing is dispatched until
           the agent sends from the Send SMS dialog itself. -->
      <div v-if="fromSms" class="feature-filter-submit mt-3">
        <PrimaryActionBtn
          :disabled="!selectedTemplate"
          @click="sendTemplateForEdit"
        >
          Edit and Send
        </PrimaryActionBtn>
      </div>

      <div
        v-if="
          isUserPermitted(PermissionNames.TICKET_UPDATE) &&
          !fromPrivateReply &&
          !fromSms
        "
        class="feature-filter-submit mt-3"
      >
        <NavButton :disabled="!selectedTemplate" @click="sendTemplateForEdit">
          Edit and Send
        </NavButton>
        <PrimaryActionBtn :disabled="!selectedTemplate" @click="sendTemplate">
          Send
        </PrimaryActionBtn>
      </div>

      <div
        v-if="
          isUserPermitted(PermissionNames.TICKET_UPDATE) &&
          fromPrivateReply &&
          !fromSms
        "
        class="feature-filter-submit mt-3"
      >
        <PrimaryActionBtn :disabled="!selectedTemplate" @click="sendTemplate">
          Use
        </PrimaryActionBtn>
      </div>
    </el-tab-pane>

    <!-- ✅ Tab 2: Order Confirmation -->
    <el-tab-pane
      v-if="!fromPrivateReply && !fromSms"
      label="Order Confirmation"
      name="order"
    >
      <!-- Gated on the dialog being open as well as the tab being selected. The
           tab does not change when the dialog closes, so the form used to outlive
           it: it stayed mounted and clickable through the closing fade, and the
           moment the submit lock came off a second click created a second order
           and a second confirmation message. It also meant reopening the dialog
           handed back the previous order's form, already filled in and armed.
           Every other place this form is mounted — Edit Draft Order and the quick
           Create Order action in MessageBox, Create Order on the orders page —
           already ties its `v-if` to the dialog's own flag, so it unmounts on the
           same tick the dialog is dismissed. This is the one that did not. -->
      <div
        v-if="
          currentTab === 'order' && replyTemplatesStore.showTemplatesListDialog
        "
      >
        <OrdersCreateForm
          :key="inboxStore.selectedTicket.id"
          :show-header="false"
          @close-modal="$emit('close-modal')"
          @set-ordermessage="$emit('set-ordermessage', $event)"
        ></OrdersCreateForm>
      </div>
    </el-tab-pane>
  </el-tabs>
</template>
